/**
 * StepIndicator Utility Functions
 *
 * Helper functions for step status calculation and styling.
 *
 * @module components/ui/StepIndicator/utils
 */

import type { StepStatus, StepConfig, StepIndicatorSize, StepIndicatorVariant } from './types';

/**
 * Calculate the status of a step based on current step
 */
export const getStepStatus = (
  stepKey: string,
  currentStep: string,
  steps: StepConfig[]
): StepStatus => {
  const stepKeys = steps.map(step => step.key);
  const currentIndex = stepKeys.indexOf(currentStep);
  const stepIndex = stepKeys.indexOf(stepKey);

  if (stepIndex < currentIndex) return 'completed';
  if (stepIndex === currentIndex) return 'current';
  return 'upcoming';
};

/**
 * Get size-specific classes for step circles
 */
export const getStepCircleClasses = (size: StepIndicatorSize): string => {
  const sizeClasses = {
    sm: 'w-8 h-8 min-w-8 min-h-8',
    md: 'w-10 h-10 min-w-10 min-h-10',
    lg: 'w-12 h-12 min-w-12 min-h-12'
  };

  return `${sizeClasses[size]} rounded-full border-2 transition-colors flex items-center justify-center flex-shrink-0`;
};

/**
 * Get size-specific classes for step icons
 */
export const getStepIconClasses = (size: StepIndicatorSize): string => {
  const iconSizes = {
    sm: 'w-6 h-6',
    md: 'w-5 h-5',
    lg: 'w-4 h-4'
  };

  return iconSizes[size];
};

/**
 * Get size-specific classes for connector lines
 */
export const getConnectorClasses = (
  size: StepIndicatorSize,
  variant: StepIndicatorVariant
): string => {
  if (variant === 'vertical') {
    const heights = {
      sm: 'h-6',
      md: 'h-8',
      lg: 'h-10'
    };
    return `w-0.5 ${heights[size]} transition-colors`;
  }

  const widths = {
    sm: 'w-6',
    md: 'w-8',
    lg: 'w-10'
  };
  return `${widths[size]} h-0.5 transition-colors`;
};

/**
 * Get spacing classes based on size and variant
 */
export const getSpacingClasses = (
  size: StepIndicatorSize,
  variant: StepIndicatorVariant
): string => {
  if (variant === 'vertical') {
    const spacing = {
      sm: 'space-y-2',
      md: 'space-y-4',
      lg: 'space-y-6'
    };
    return spacing[size];
  }

  const spacing = {
    sm: 'space-x-2 sm:space-x-4',
    md: 'space-x-4 sm:space-x-8',
    lg: 'space-x-6 sm:space-x-10'
  };
  return spacing[size];
};

/**
 * Get text size classes based on size variant
 */
export const getTextSizeClasses = (size: StepIndicatorSize) => {
  const textSizes = {
    sm: {
      title: 'text-xs font-medium',
      description: 'text-xs'
    },
    md: {
      title: 'text-sm font-medium',
      description: 'text-xs'
    },
    lg: {
      title: 'text-base font-medium',
      description: 'text-sm'
    }
  };

  return textSizes[size];
};

/**
 * Get layout classes for the step indicator container
 */
export const getContainerClasses = (variant: StepIndicatorVariant): string => {
  if (variant === 'vertical') {
    return 'flex flex-col';
  }

  return 'flex justify-center';
};

/**
 * Get layout classes for the steps list
 */
export const getStepsListClasses = (
  variant: StepIndicatorVariant,
  size: StepIndicatorSize
): string => {
  const baseClasses = 'flex items-center';
  const spacingClasses = getSpacingClasses(size, variant);

  if (variant === 'vertical') {
    return `${baseClasses} flex-col ${spacingClasses}`;
  }

  return `${baseClasses} ${spacingClasses}`;
};